import { Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import {
  fmtDate,
  getResultStatus,
  isRankDisplayable,
} from "@/components/athlete/formatters";
import type { AthleteResult } from "@/components/athlete/types";

export function RaceResultsList({ results }: { results: AthleteResult[] }) {
  const navigate = useNavigate();

  return (
    <section className="d8-reveal d8-reveal-delay-3 revealed athlete-results-section">
      <h2 className="athlete-section-title">Race Results ({results.length})</h2>
      <div className="athlete-results-grid">
        {results.map((result) => {
          const hideRank =
            result.dnf ||
            result.dns ||
            result.dq ||
            !isRankDisplayable(result.finishRankOverall);
          const highlightRank = hideRank
            ? undefined
            : (result.finishRankOverall ?? undefined);

          return (
            <Card
              key={result.id}
              className="athlete-result-card"
              role="link"
              tabIndex={0}
              aria-label={`View result details for ${result.eventName}`}
              onClick={(event) => {
                if (
                  (event.target as HTMLElement).closest(
                    "a,button,input,select,textarea,[role='button']",
                  )
                ) {
                  return;
                }
                void navigate({
                  to: "/results/$resultId",
                  params: { resultId: result.id },
                });
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                void navigate({
                  to: "/results/$resultId",
                  params: { resultId: result.id },
                });
              }}
            >
              <CardContent className="athlete-result-card-content">
                <div className="athlete-result-top-row">
                  <div className="athlete-result-main">
                    <Link
                      to="/events/$eventId"
                      params={{ eventId: result.eventId }}
                      search={{
                        ageGroup: undefined,
                        highlight: result.id,
                        highlightRank,
                        page: undefined,
                      }}
                      className="athlete-result-title-link"
                    >
                      {result.eventName}
                    </Link>

                    <p className="athlete-result-meta">
                      {fmtDate(result.eventDate)}
                      {result.eventBrand && ` · ${result.eventBrand}`}
                      {result.ageGroup && (
                        <>
                          {" · "}
                          <Link
                            to="/events/$eventId"
                            params={{ eventId: result.eventId }}
                            search={{
                              ageGroup: result.ageGroup,
                              highlight: result.id,
                              highlightRank,
                              page: undefined,
                            }}
                            className="athlete-result-age-group-link"
                          >
                            {result.ageGroup}
                          </Link>
                        </>
                      )}
                      {result.bib && ` · Bib #${result.bib}`}
                    </p>
                  </div>

                  <div className="athlete-result-performance">
                    <p className="athlete-result-time">
                      {result.finishTimeFormatted ?? "—"}
                    </p>
                    <p className="athlete-result-status">
                      {getResultStatus(result)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
